import { applyPct } from "./arith";
import { isValidCNPJ, isValidCPF, normalizeDocument } from "./documents";
import { DomainError } from "./errors";
import { MAX_TITLE_CENTS } from "./finance";
import { centsToDecimal, nfeDateTime, pct4ToDecimal } from "./nfe";
import { normalizeEventText } from "./nfe-events";

// ===== NFS-e: DPS do Sistema Nacional (Nuvem Fiscal `POST /nfse/dps`), RF-SER-04 =====

/** Natureza da operação (`tribISSQN`): 1 tributável, 2 imunidade, 3 exportação, 4 não incidência. */
export const NFSE_NATURES = [1, 2, 3, 4] as const;
export type NfseNature = (typeof NFSE_NATURES)[number];
/** Tipo da imunidade (`tpImunidade` 0–5), só na natureza 2. */
export const NFSE_IMMUNITIES = [0, 1, 2, 3, 4, 5] as const;
/** Regime especial de tributação do padrão nacional (`regEspTrib` 0–6). */
export const NFSE_SPECIAL_REGIMES = [0, 1, 2, 3, 4, 5, 6] as const;
/** `provedor` da Nuvem Fiscal: Sistema Nacional (ADN) ou o provedor da prefeitura (ABRASF e outros). */
export const NFSE_PROVIDER_MODES = ["nacional", "padrao"] as const;
export type NfseProviderMode = (typeof NFSE_PROVIDER_MODES)[number];
/** Código de tributação nacional do ISSQN: item (2) + subitem (2) da LC 116 + desdobro nacional (2). */
export const NFSE_CTRIBNAC_RE = /^\d{6}$/;
export const NFSE_MAX_DESCRIPTION = 2000;
export const NFSE_SOFTWARE = "CX ERP 1.0";

export type NfseRetentionsPct4 = {
  pisPct4: number;
  cofinsPct4: number;
  csllPct4: number;
  irPct4: number;
  inssPct4: number;
};
export const ZERO_RETENTIONS: NfseRetentionsPct4 = {
  pisPct4: 0,
  cofinsPct4: 0,
  csllPct4: 0,
  irPct4: 0,
  inssPct4: 0,
};

export type NfseValuesInput = {
  servicesCents: number;
  /** Desconto incondicionado (sai da base do ISS e do valor líquido). */
  discountCents: number;
  /** Deduções/reduções da base do ISS (materiais, subempreitada…): só a base do ISS. */
  deductionsCents: number;
  nature: NfseNature;
  /** null = alíquota do município (o Sistema Nacional parametriza) e ISS calculado lá. */
  issPct4: number | null;
  issWithheld: boolean;
  retentions: NfseRetentionsPct4;
};

export type NfseValues = {
  /** Base do ISS = serviços − desconto incondicionado − deduções. */
  baseCents: number;
  issCents: number;
  /** ISS retido pelo tomador (sai do líquido). */
  issWithheldCents: number;
  pisCents: number;
  cofinsCents: number;
  csllCents: number;
  irCents: number;
  inssCents: number;
  /** Soma das retenções federais. */
  retentionsCents: number;
  /** Líquido = serviços − desconto − ISS retido − retenções federais. */
  netCents: number;
};

const money = (v: number, what: string) => {
  if (!Number.isSafeInteger(v) || v < 0 || v > MAX_TITLE_CENTS)
    throw new DomainError("invalid_money", `${what} inválido`);
  return BigInt(v);
};
const pct = (v: number, what: string) => {
  if (!Number.isSafeInteger(v) || v < 0 || v > 1_000_000)
    throw new DomainError("invalid_pct", `${what} inválida`);
  return v;
};

/**
 * Valores da NFS-e em centavos (BigInt nas somas; `applyPct` arredonda meio para cima). ISS só na natureza
 * tributável e com alíquota informada; retenções federais sobre serviços − desconto incondicionado.
 */
export function computeNfseValues(i: NfseValuesInput): NfseValues {
  const services = money(i.servicesCents, "Valor dos serviços");
  const discount = money(i.discountCents, "Desconto");
  const deductions = money(i.deductionsCents, "Deduções");
  if (services <= 0n) throw new DomainError("invalid_money", "Informe o valor dos serviços");
  const gross = services - discount;
  if (gross < 0n) throw new DomainError("invalid_money", "O desconto passa do valor dos serviços");
  const base = gross - deductions;
  if (base < 0n) throw new DomainError("invalid_money", "As deduções passam do valor dos serviços");
  const iss =
    i.nature === 1 && i.issPct4 !== null
      ? BigInt(applyPct(Number(base), pct(i.issPct4, "Alíquota do ISS")))
      : 0n;
  const r = i.retentions;
  const of = (p: number, what: string) => BigInt(applyPct(Number(gross), pct(p, what)));
  const pis = of(r.pisPct4, "Alíquota do PIS");
  const cofins = of(r.cofinsPct4, "Alíquota da COFINS");
  const csll = of(r.csllPct4, "Alíquota da CSLL");
  const ir = of(r.irPct4, "Alíquota do IR");
  const inss = of(r.inssPct4, "Alíquota do INSS");
  const retentions = pis + cofins + csll + ir + inss;
  const withheld = i.issWithheld ? iss : 0n;
  const net = gross - withheld - retentions;
  if (net < 0n) throw new DomainError("invalid_money", "As retenções passam do valor dos serviços");
  return {
    baseCents: Number(base),
    issCents: Number(iss),
    issWithheldCents: Number(withheld),
    pisCents: Number(pis),
    cofinsCents: Number(cofins),
    csllCents: Number(csll),
    irCents: Number(ir),
    inssCents: Number(inss),
    retentionsCents: Number(retentions),
    netCents: Number(net),
  };
}

export type NfseAddress = {
  street: string;
  number: string;
  complement: string | null;
  district: string;
  cityIbge: string;
  cep: string | null;
};
export type NfseService = {
  cTribNac: string;
  cTribMun: string | null;
  cnae: string | null;
  nbs: string | null;
  description: string;
};
export type NfseInput = {
  environment: 1 | 2;
  issuer: { cnpj: string; cityIbge: string };
  recipient: {
    document: string;
    name: string;
    im: string | null;
    email: string | null;
    phone: string | null;
    address: NfseAddress | null;
  };
  service: NfseService;
  nature: NfseNature;
  immunityType: number | null;
  /** País do resultado na exportação (ISO 3166 alfa-2). */
  exportCountry: string | null;
  values: NfseValuesInput;
  /** CST do PIS/COFINS no grupo das retenções. */
  pisCofinsCst: string;
  complementaryInfo: string | null;
  /** Documento de referência (nº do contrato…). */
  docRef: string | null;
  /**
   * Competência ("AAAA-MM") da cobrança de contrato que gerou a nota; null = avulsa (dCompet = data da emissão).
   */
  competence: string | null;
};

const IBGE_RE = /^\d{7}$/;

/** Problemas de cadastro da NFS-e (mensagens pt-BR; lista vazia = pode emitir). */
export function nfseProblems(i: NfseInput): string[] {
  const p: string[] = [];
  const cnpj = normalizeDocument(i.issuer.cnpj);
  if (!isValidCNPJ(cnpj)) p.push("CNPJ da empresa inválido (Preferências › Dados da empresa)");
  if (!IBGE_RE.test(i.issuer.cityIbge)) p.push("Código IBGE do município da empresa ausente");
  const doc = normalizeDocument(i.recipient.document);
  if (!(doc.length === 11 ? isValidCPF(doc) : isValidCNPJ(doc)))
    p.push("Tomador sem CPF/CNPJ válido (cadastre o documento do cliente)");
  if (!i.recipient.name.trim()) p.push("Nome do tomador ausente");
  const a = i.recipient.address;
  if (a && (!a.street.trim() || !a.district.trim() || !IBGE_RE.test(a.cityIbge)))
    p.push("Endereço do tomador incompleto (logradouro, bairro e município com código IBGE)");
  if (!NFSE_CTRIBNAC_RE.test(i.service.cTribNac))
    p.push("Código de tributação nacional do serviço com 6 dígitos (item, subitem e desdobro da LC 116)");
  if (i.service.cnae !== null && !/^\d{7}$/.test(i.service.cnae)) p.push("CNAE com 7 dígitos");
  if (i.service.nbs !== null && !/^\d{9}$/.test(i.service.nbs)) p.push("Código NBS com 9 dígitos");
  if (!normalizeEventText(i.service.description)) p.push("Descreva o serviço prestado");
  if (i.service.description.length > NFSE_MAX_DESCRIPTION)
    p.push(`A descrição do serviço aceita até ${NFSE_MAX_DESCRIPTION} caracteres`);
  if (i.nature === 2 && i.immunityType === null) p.push("Informe o tipo da imunidade");
  if (i.nature === 3 && !/^[A-Z]{2}$/.test(i.exportCountry ?? ""))
    p.push("Informe o país do resultado do serviço exportado");
  try {
    computeNfseValues(i.values);
  } catch (e) {
    p.push((e as DomainError).message);
  }
  return p;
}

const clean = (s: string, max: number) => normalizeEventText(s).slice(0, max);
const digits = (s: string | null) => (s ?? "").replace(/\D/g, "");
const dec = (cents: number) => centsToDecimal(cents);

/**
 * `infDPS` do pedido `POST /nfse/dps` (valores em decimal; datas em −03:00). Problema de cadastro = DomainError
 * `nfse_incomplete` (a rota já confere antes; aqui é a última barreira).
 */
export function buildDpsPayload(i: NfseInput, issuedAt: Date): Record<string, unknown> {
  const problems = nfseProblems(i);
  if (problems.length) throw new DomainError("nfse_incomplete", problems.join("; "));
  const v = computeNfseValues(i.values);
  const doc = normalizeDocument(i.recipient.document);
  const a = i.recipient.address;
  const toma: Record<string, unknown> = {
    ...(doc.length === 11 ? { CPF: doc } : { CNPJ: doc }),
    ...(i.recipient.im ? { IM: digits(i.recipient.im) } : {}),
    xNome: clean(i.recipient.name, 150),
    ...(a
      ? {
          end: {
            endNac: { cMun: a.cityIbge, ...(digits(a.cep).length === 8 ? { CEP: digits(a.cep) } : {}) },
            xLgr: clean(a.street, 255),
            nro: clean(a.number || "S/N", 60),
            ...(a.complement?.trim() ? { xCpl: clean(a.complement, 156) } : {}),
            xBairro: clean(a.district, 60),
          },
        }
      : {}),
    ...(digits(i.recipient.phone).length >= 10 ? { fone: digits(i.recipient.phone).slice(0, 20) } : {}),
    ...(i.recipient.email ? { email: i.recipient.email.trim().slice(0, 80) } : {}),
  };
  const s = i.service;
  const infoCompl = {
    ...(i.docRef ? { docRef: clean(i.docRef, 255) } : {}),
    ...(i.complementaryInfo?.trim() ? { xInfComp: clean(i.complementaryInfo, 2000) } : {}),
  };
  const r = i.values.retentions;
  const tribFed: Record<string, unknown> = {
    ...(v.pisCents > 0 || v.cofinsCents > 0
      ? {
          piscofins: {
            CST: i.pisCofinsCst,
            vBCPisCofins: dec(v.baseCents + i.values.deductionsCents),
            pAliqPis: pct4ToDecimal(r.pisPct4),
            pAliqCofins: pct4ToDecimal(r.cofinsPct4),
            vPis: dec(v.pisCents),
            vCofins: dec(v.cofinsCents),
            tpRetPisCofins: 1,
          },
        }
      : {}),
    ...(v.inssCents > 0 ? { vRetCP: dec(v.inssCents) } : {}),
    ...(v.irCents > 0 ? { vRetIRRF: dec(v.irCents) } : {}),
    ...(v.csllCents > 0 ? { vRetCSLL: dec(v.csllCents) } : {}),
  };
  const tribMun: Record<string, unknown> = {
    tribISSQN: i.nature,
    ...(i.nature === 2 ? { tpImunidade: i.immunityType } : {}),
    ...(i.nature === 3 ? { cPaisResult: i.exportCountry } : {}),
    ...(i.nature === 1 && i.values.issPct4 !== null
      ? { vBC: dec(v.baseCents), pAliq: pct4ToDecimal(i.values.issPct4), vISSQN: dec(v.issCents) }
      : {}),
    tpRetISSQN: i.nature === 1 && i.values.issWithheld ? 2 : 1,
    vLiq: dec(v.netCents),
  };
  return {
    tpAmb: i.environment,
    dhEmi: nfeDateTime(issuedAt),
    verAplic: NFSE_SOFTWARE,
    dCompet: nfseCompetenceDate(i.competence, issuedAt),
    prest: { CNPJ: normalizeDocument(i.issuer.cnpj) },
    toma,
    serv: {
      locPrest: { cLocPrestacao: i.issuer.cityIbge },
      cServ: {
        cTribNac: s.cTribNac,
        ...(s.cTribMun?.trim() ? { cTribMun: clean(s.cTribMun, 20) } : {}),
        ...(s.cnae ? { CNAE: s.cnae } : {}),
        xDescServ: clean(s.description, NFSE_MAX_DESCRIPTION),
        ...(s.nbs ? { cNBS: s.nbs } : {}),
      },
      ...(Object.keys(infoCompl).length ? { infoCompl } : {}),
    },
    valores: {
      vServPrest: { vServ: dec(i.values.servicesCents) },
      ...(i.values.discountCents > 0
        ? { vDescCondIncond: { vDescIncond: dec(i.values.discountCents) } }
        : {}),
      ...(i.values.deductionsCents > 0 ? { vDedRed: { vDR: dec(i.values.deductionsCents) } } : {}),
      trib: {
        tribMun,
        ...(Object.keys(tribFed).length ? { tribFed } : {}),
        totTrib: { indTotTrib: 0 },
      },
    },
  };
}

/**
 * `dCompet` (AAAA-MM-DD) da DPS: na cobrança de contrato, o 1º dia do mês da competência; na avulsa, o dia da emissão
 * em São Paulo. O leiaute recusa competência depois da emissão, então a cobrança do mês seguinte (gerada adiantada)
 * fica com o dia da emissão.
 */
export function nfseCompetenceDate(competence: string | null, issuedAt: Date): string {
  const issued = nfeDateTime(issuedAt).slice(0, 10);
  if (!competence || !/^\d{4}-(0[1-9]|1[0-2])$/.test(competence)) return issued;
  const first = `${competence}-01`;
  return first < issued ? first : issued;
}

/** Optante do Simples (`opSimpNac`) pelo CRT da empresa: 1 → ME/EPP (3), 4 → MEI (2), demais → não optante (1). */
export const nfseSimplesOption = (crt: number | null): 1 | 2 | 3 => (crt === 1 ? 3 : crt === 4 ? 2 : 1);

/** Motivo do cancelamento (15–255, texto do leiaute) ou null. */
export function nfseCancelReasonProblem(raw: string): string | null {
  const s = normalizeEventText(raw);
  if (s.length < 15) return "O motivo precisa ter ao menos 15 caracteres";
  if (s.length > 255) return "O motivo aceita até 255 caracteres";
  return null;
}
